// ABOUTME: The sky strip: two viewport-wide panels side by side, a storm panel with rain across its width, then the storm row.
// ABOUTME: Translating the strip by x shows the storm (0) or the storm row (1), so clouds and their rain pass the screen together.

import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { CROSSING_STORM_SKY_TOP, CROSSING_STORM_SKY_BOTTOM, CROSSING_STORM_VEIL_DROP } from '../../../theme/crossingTokens';
import StormHorizon from './StormHorizon';
import {
  CLOUD_SHAPES, SKY_ABOVE_STAGE, SKY_H, SLIDE_CLOUDS, STORM_LAYER_CLOUDS, STORM_SKY_GRADIENT, pvRainVeil,
} from './stormSkyParts';

const pvDropsIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
// The whole round's pass: the strip moves from the storm panel to the storm row at a steady pace.
const breathSkyDrift = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;
const ROLL_IN_SECONDS = 2.3;

// The storm panel's five large clouds: a cloud from the other storm sets, its centre across the panel (%) and its top (% of the sky).
const STORM_PANEL_CLOUDS = [
  { cloud: SLIDE_CLOUDS[0], centre: '12%', top: '24%' },
  { cloud: SLIDE_CLOUDS[1], centre: '30%', top: '52%' },
  { cloud: STORM_LAYER_CLOUDS[0], centre: '46%', top: '36%' },
  { cloud: SLIDE_CLOUDS[2], centre: '64%', top: '28%' },
  { cloud: STORM_LAYER_CLOUDS[1], centre: '84%', top: '50%' },
];

interface SkyStripProps {
  x: number; // 0 shows the storm panel, 1 the storm row
  drift?: number; // seconds: the strip passes from 0 to 1 over this long by itself, and x is ignored; 0 holds the strip at x
  rollIn?: boolean; // the strip is arriving: its rain fades in over the roll
  reducedMotion?: boolean;
}

// Fills its parent, which is a box one panel wide and as tall as the sky and clips what is off to either side.
export default function SkyStrip({ x, drift = 0, rollIn = false, reducedMotion = false }: SkyStripProps) {
  const drifting = drift > 0 && !reducedMotion;
  return (
    <Box
      data-testid="breath-sky-strip"
      // While it drifts the animation owns the transform, so the strip is not rewritten every second.
      style={drifting ? undefined : { transform: `translateX(${-x * 50}%)` }}
      sx={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: '200%',
        ...(drifting && { animationName: `${breathSkyDrift}`, animationDuration: `${drift}s`, animationTimingFunction: 'linear', animationFillMode: 'forwards' }),
      }}
    >
      <Box
        data-testid="breath-sky-panel-storm"
        sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: '50%',
          background: `linear-gradient(${CROSSING_STORM_SKY_TOP} ${SKY_ABOVE_STAGE}px, ${CROSSING_STORM_SKY_BOTTOM} ${SKY_ABOVE_STAGE + SKY_H}px)`,
        }}
      >
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${SKY_H}px` }}>
          <Box
            data-testid="breath-roll-veil"
            sx={{ position: 'absolute', left: '-15%', right: '-15%', top: 0, bottom: 0, transform: 'skewX(-16deg)' }}
          >
            <Box
              data-testid="breath-roll-drops"
              sx={{
                position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                backgroundImage: `radial-gradient(ellipse 1px 6px at 50% 50%, ${CROSSING_STORM_VEIL_DROP} 96%, transparent 100%)`, backgroundSize: '16px 34px',
                // The rain falls forever; while the strip arrives it also fades in over the roll.
                animationName: reducedMotion ? 'none' : rollIn ? `${pvRainVeil}, ${pvDropsIn}` : `${pvRainVeil}`,
                animationDuration: rollIn ? `1.5s, ${ROLL_IN_SECONDS}s` : '1.5s',
                animationTimingFunction: rollIn ? 'linear, ease' : 'linear',
                animationIterationCount: rollIn ? 'infinite, 1' : 'infinite',
                animationFillMode: rollIn ? 'none, backwards' : 'none',
              }}
            />
          </Box>
          {STORM_PANEL_CLOUDS.map(({ cloud, centre, top }) => (
            <Box
              key={`${cloud.fill}-${centre}`}
              data-testid="breath-sky-panel-storm-cloud"
              style={{ opacity: cloud.opacity }}
              sx={{ position: 'absolute', left: centre, top, width: `${cloud.width}px`, height: `${cloud.height}px`, transform: 'translateX(-50%)' }}
            >
              {CLOUD_SHAPES.map(shape => (
                <Box key={shape.left} sx={{ position: 'absolute', ...shape, background: cloud.fill }} />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        data-testid="breath-sky-panel-horizon"
        sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '50%', background: STORM_SKY_GRADIENT }}
      >
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${SKY_H}px` }}>
          <StormHorizon reducedMotion={reducedMotion} />
        </Box>
      </Box>
    </Box>
  );
}
